/* ============================================================
   JGNT Quick Add  · v3  (Oct 10 2026)
   Comportamiento de snippets/jgnt-quick-add.liquid.

   Un solo listener delegado en document. Asi funciona igual en
   las tarjetas del home y en los recomendados del carrito, que se
   vuelven a pintar completos cada vez que el carrito cambia.

   AGREGA POR EL MISMO CAMINO QUE LA PAGINA DE PRODUCTO
   (assets/product-form.js):

     1. Avisa con CartLinesUpdateEvent de @shopify/events y una
        promesa pendiente. Con eso el icono del carrito
        (cart-icon.js) y la lista del cajon (component-cart-items.js)
        se enteran solos: no hay que tocarlos desde aqui.
     2. POST a /cart/add.js pidiendo de regreso el HTML de las
        secciones del carrito (parametro sections).
     3. Lee /cart.js para el total real y resuelve la promesa con
        el carrito y ese HTML.

   Si Shopify rechaza la variante (sin inventario, por ejemplo) la
   promesa se rechaza, el boton muestra el error un momento y
   vuelve a su estado.

   ------------------------------------------------------------
   v2 - SE SIENTE INMEDIATO

   Medido en la tienda: /cart/add.js pidiendo el HTML del carrito
   tardaba ~800ms, y luego /cart.js otros ~200ms. Un segundo entero
   sin que pasara nada en pantalla.

   1. RESPUESTA OPTIMISTA. Al tocar, sin esperar al servidor, el
      numero del icono sube uno. Si el servidor rechaza, el numero
      regresa y el boton marca error. (v3: ya no hay estado de
      agregado en el boton ni pop en la burbuja.)

   2. SIN EL HTML DEL CARRITO cuando el carrito esta cerrado (el
      caso del home). Pedirlo le cuesta al servidor ~300ms de
      render. Sin el, component-cart-items.js ve que no llego y lo
      pide por su cuenta en segundo plano; para cuando abras el
      carrito ya esta al dia. Con el carrito ABIERTO (los
      recomendados de adentro) si se pide, porque ahi estas viendo
      la lista y tiene que cambiar junto con el boton.

   ------------------------------------------------------------
   v3 - SIN ANIMACIONES AL AGREGAR

   Al agregar ya no cambia el boton (ni palomita en el +, ni
   "Agregando..." / "Agregado" en el boton ancho). Lo unico que se
   mueve es el numero del icono del carrito, y sin pop
   (assets/cart-icon.js). El panel de tallas se cierra al elegir.

   Si Shopify RECHAZA el producto el boton si avisa con el error un
   momento: sin eso el cliente creeria que se agrego.
   ============================================================ */

import { CartLinesUpdateEvent } from '@shopify/events';

const ADDED_MS = 1600;
const ERROR_MS = 2200;

/** @param {HTMLElement} root */
function closePanel(root) {
  root.classList.remove('is-open');
  root.querySelector('[data-jgnt-qa-trigger]')?.setAttribute('aria-expanded', 'false');
}

function closeAll(except) {
  document.querySelectorAll('[data-jgnt-qa].is-open').forEach((root) => {
    if (root !== except) closePanel(/** @type {HTMLElement} */ (root));
  });
}

/**
 * @param {HTMLElement} root
 * @param {'added' | 'error'} state
 * @param {string} [message]
 */
function flash(root, state, message) {
  const label = root.querySelector('[data-jgnt-qa-label]');
  const original = root.dataset.tAdd || '';

  root.classList.add(state === 'added' ? 'is-added' : 'is-error');
  if (label) label.textContent = state === 'added' ? root.dataset.tAdded || '' : message || root.dataset.tError || '';

  window.setTimeout(
    () => {
      root.classList.remove('is-added', 'is-error');
      if (label && label.isConnected) label.textContent = original;
    },
    state === 'added' ? ADDED_MS : ERROR_MS
  );
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} button - el que se toco, para marcarlo ocupado
 * @param {string} variantId
 */
async function addToCart(root, button, variantId) {
  if (button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');

  /* v2: el HTML del carrito solo se pide si se esta viendo. */
  const drawerOpen = Boolean(document.querySelector('#cart-drawer[open]'));
  const sectionIds = drawerOpen
    ? Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
        .filter(Boolean)
    : [];

  /* v2: respuesta optimista. */
  const previousCount = readCartCount();
  setCartCount(previousCount + 1);
  showPending(root);

  const deferred = CartLinesUpdateEvent.createPromise();

  root.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', '1');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    }).then((r) => r.json());

    if (response.status) {
      throw new Error(response.description || response.message || 'Add to cart failed');
    }

    const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: 1,
        source: 'jgnt-quick-add',
        sections: response.sections,
        didError: false,
      },
    });

    button.removeAttribute('aria-busy');
  } catch (error) {
    deferred.reject(error);
    setCartCount(previousCount);
    button.removeAttribute('aria-busy');
    if (root.isConnected) {
      flash(root, 'error', error instanceof Error ? error.message : undefined);
    }
  }
}

/* ---------- v2: ayudantes de la respuesta optimista ---------- */

function readCartCount() {
  const text = document.querySelector('cart-icon .cart-bubble__text-count')?.textContent?.trim();
  const count = parseInt(text || '0', 10);
  return Number.isFinite(count) ? count : 0;
}

/**
 * Cambia el numero del icono sin animarlo. renderCartBubble es el
 * mismo metodo que usa cart-icon.js; el pop lo hace el propio
 * icono cuando llega la confirmacion del servidor.
 * @param {number} count
 */
function setCartCount(count) {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    const render = /** @type {any} */ (icon).renderCartBubble;
    if (typeof render === 'function') render(Math.max(0, count), false);
  });
}

/**
 * v3: solo cierra el panel de tallas. El boton ya no cambia.
 * @param {HTMLElement} root
 */
function showPending(root) {
  closePanel(root);
}

if (!window.jgntQuickAddReady) {
  window.jgntQuickAddReady = true;

  document.addEventListener('click', (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    const root = /** @type {HTMLElement | null} */ (target?.closest('[data-jgnt-qa]') ?? null);

    if (!root) {
      closeAll();
      return;
    }

    /* Que el toque no llegue al enlace de la tarjeta. */
    event.preventDefault();
    event.stopPropagation();

    const option = /** @type {HTMLElement | null} */ (target?.closest('[data-jgnt-qa-opt]') ?? null);
    if (option) {
      if (!option.hasAttribute('disabled') && option.dataset.variantId) {
        addToCart(root, option, option.dataset.variantId);
      }
      return;
    }

    if (target?.closest('[data-jgnt-qa-close]')) {
      closePanel(root);
      return;
    }

    const trigger = /** @type {HTMLElement | null} */ (target?.closest('[data-jgnt-qa-trigger]') ?? null);
    if (!trigger) return;

    /* Una sola variante: directo al carrito. */
    if (trigger.dataset.variantId) {
      addToCart(root, trigger, trigger.dataset.variantId);
      return;
    }

    const open = !root.classList.contains('is-open');
    closeAll(root);
    root.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', String(open));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });
}
